import { useState, useEffect } from "react";
import { FileText, RefreshCw, ChevronDown, ChevronUp, Activity, TrendingUp, TrendingDown, AlertTriangle, ChevronRight, Compass, Calendar, Shield, ShieldAlert, Skull } from "lucide-react";
import axios from "axios";
import { briefingDailyAPI, marketAPI } from "../services/api";
import BriefChatPanel from "./BriefChatPanel";
import { DeskWatchlist } from "./DeskWatchlist";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DailyBriefing({ owner, marketTiming, onAnalyzeTicker, onTabChange, briefingCache, setBriefingCache }) {
  // Use parent cache for persistence across tab switches — keyed by owner
  const cachedOwner = briefingCache?.owner || "";
  const briefing = (cachedOwner === owner) ? (briefingCache?.briefing || "") : "";
  const sources = (cachedOwner === owner) ? (briefingCache?.sources || []) : [];
  const loaded = (cachedOwner === owner) ? (briefingCache?.loaded || false) : false;
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [upcomingEarnings, setUpcomingEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  // 2026-06-17 — Claude can take 2-3 min for large briefings. Cloudflare LB drops at 60s.
  // We poll every 25s when force=true so the cached result lands as soon as backend finishes.
  const fetchBriefing = async (force = false) => {
    if (!owner) return;
    setLoading(true);
    try {
      const data = await briefingDailyAPI.get(owner, force);
      // Empty briefing AND we asked for force → backend is still generating. Poll cache.
      if (force && (!data?.briefing || data.briefing.length < 200)) {
        // Show transient message while polling
        if (setBriefingCache) setBriefingCache({
          briefing: "⏳ Briefing en cours de génération via Claude (peut prendre 1-3 min)...\n\nNe ferme pas la page — il s'affichera automatiquement dès qu'il est prêt.",
          sources: [], loaded: true, owner,
        });
        // Poll every 25s, max 8 retries (= 200s ceiling)
        let attempts = 0;
        const maxAttempts = 8;
        const poll = async () => {
          attempts++;
          try {
            const d2 = await briefingDailyAPI.get(owner, false); // no force = read cache
            if (d2?.briefing && d2.briefing.length > 200 && !d2.briefing.startsWith("⏳")) {
              const newCache = { briefing: d2.briefing, sources: d2.data_sources || [], loaded: true, owner };
              if (setBriefingCache) setBriefingCache(newCache);
              setLoading(false);
              return;
            }
          } catch (e) { /* ignore polling errors */ }
          if (attempts < maxAttempts) {
            setTimeout(poll, 25000);
          } else {
            setLoading(false);
          }
        };
        setTimeout(poll, 25000);
        return;
      }
      const newCache = { briefing: data.briefing || "", sources: data.data_sources || [], loaded: true, owner };
      if (setBriefingCache) setBriefingCache(newCache);
    } catch (e) {
      console.error("Briefing fetch error", e);
    } finally {
      // Only release loading if we're not in polling mode (polling sets its own loading=false)
      if (!force) setLoading(false);
    }
  };

  const fetchUpcomingEarnings = async () => {
    if (!owner) return;
    setEarningsLoading(true);
    try {
      const r = await axios.get(`${API}/api/briefing/upcoming-earnings/${owner}?days=10`, { timeout: 90000 });
      setUpcomingEarnings(r.data);
    } catch (e) {
      console.error("Upcoming earnings error", e);
    } finally {
      setEarningsLoading(false);
    }
  };

  // Auto-fetch when owner changes or cache is empty — use cached version first (no force)
  useEffect(() => {
    if (!loaded && owner && !loading) {
      fetchBriefing(false);
    }
  }, [owner, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch upcoming earnings when expanded
  useEffect(() => {
    if (expanded && owner && !upcomingEarnings && !earningsLoading) {
      fetchUpcomingEarnings();
    }
  }, [expanded, owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const mt = marketTiming;
  const vix = mt?.indicators?.vix;
  const rsi = mt?.indicators?.sp500_rsi;
  const spWeek = mt?.indicators?.sp500_week_change;
  const verdict = mt?.verdict;
  const verdictColor = mt?.verdict_color;

  // 🆕 2026-06-04 — CRASH STATUS (inline indicator next to VIX)
  // Derives a 4-level crash risk from the data already in marketTiming, and
  // tells the user EXACTLY what to liquidate first if it heats up.
  const [crashOpen, setCrashOpen] = useState(false);
  const [crashActions, setCrashActions] = useState(null);
  const [crashActionsLoading, setCrashActionsLoading] = useState(false);

  // Fetch personalized crash actions when popover opens
  useEffect(() => {
    if (!crashOpen || !owner || crashActions) return;
    setCrashActionsLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefing/crash-actions/${owner}`)
      .then((r) => r.json())
      .then((d) => setCrashActions(d))
      .catch(() => {})
      .finally(() => setCrashActionsLoading(false));
  }, [crashOpen, owner, crashActions]);
  const crashStatus = (() => {
    const vixN = Number(vix) || 0;
    const score = Number(mt?.timing_score) || 50;
    const week = Number(spWeek) || 0;
    // Score levels — calibré sur historique 2018-2025
    if (vixN >= 35 || (vixN >= 28 && week <= -5) || score < 20) {
      return {
        level: "CRASH",
        color: "bg-red-600 text-white border-red-500",
        icon: Skull,
        label: "CRASH EN COURS",
        summary: "Capitulation. Cash 70%+, garder uniquement defensives (KO, JNJ, gold).",
        actions: [
          "Liquider TOUTES les microcaps (<$2B) & meme stocks (SPCE, LMND, ASTS, etc.)",
          "Couper 70% de NVDA, AMD, ARM, PLTR (tech vol-élevée)",
          "Garder defensives: AAPL/MSFT/GOOGL (qualité), KO/PG/JNJ (staples)",
          "Acheter VIX puts ou SQQQ pour hedge si pas déjà fait",
        ],
      };
    }
    if (vixN >= 25 || (vixN >= 20 && week <= -3) || score < 35) {
      return {
        level: "ALERTE",
        color: "bg-orange-600 text-white border-orange-500",
        icon: ShieldAlert,
        label: "ALERTE",
        summary: "Marché stresse. Réduire 30-50% des positions growth.",
        actions: [
          "Couper 50% sur tickers RSI > 70 (overbought): ARM, IBM, AAPL si applicable",
          "Couper 30% sur tech mid-cap (HIMS, AEHR, etc.)",
          "Resserrer stops à -3% sur TOUTES les positions",
          "Ne PAS prendre nouvelles entrées growth avant VIX < 22",
        ],
      };
    }
    if (vixN >= 20 || (vixN >= 17 && week <= -2) || score < 55) {
      return {
        level: "VIGILANCE",
        color: "bg-amber-500 text-amber-950 border-amber-400",
        icon: AlertTriangle,
        label: "VIGILANCE",
        summary: "Volatilité monte. Trailing stops + réserve cash 15%.",
        actions: [
          "Vérifier que TOUTES les positions ont un trailing stop ATR x 2.5",
          "Cash réserve à 15-20% (pas plus de positions full leverage)",
          "Surveiller plays microcaps - sortir si -5% sans bounce",
          "Corrections -8/-16% normales en zone vigilance, ne pas paniquer",
        ],
      };
    }
    return {
      level: "CALME",
      color: "bg-emerald-500 text-emerald-950 border-emerald-400",
      icon: Shield,
      label: "CALME",
      summary: "Pas de crash en vue. Marché sain.",
      actions: [
        "Continuer le plan normal — entrées sur pullbacks A+",
        "Les pullbacks -8/-16% sont des opportunités, pas une raison de paniquer",
        "Re-checker si VIX casse au-dessus de 20",
      ],
    };
  })();
  const CrashIcon = crashStatus.icon;

  const colorCls = verdictColor === "green" ? "text-emerald-400" : verdictColor === "amber" ? "text-amber-400" : verdictColor === "orange" ? "text-orange-400" : "text-red-400";
  const borderCls = verdictColor === "green" ? "border-emerald-500/30" : verdictColor === "amber" ? "border-amber-500/30" : verdictColor === "orange" ? "border-orange-500/30" : "border-red-500/30";

  const globalIndices = mt?.indicators?.global_indices || [];

  const flagEmoji = (flag) => {
    const flags = { US: "🇺🇸", FR: "🇫🇷", DE: "🇩🇪", UK: "🇬🇧", CN: "🇨🇳" };
    return flags[flag] || "";
  };

  // Regex to detect stock tickers: AAPL, NVDA, HAG.DE, CARL-B.CO, 005930.KS, 6902.T, 1024.HK, BTC-USD
  const tickerRegex = /\b([A-Z]{1,5}(?:\.[A-Z]{1,2})?(?:-[A-Z]{1,2}(?:\.[A-Z]{2})?)?)\b|\b(\d{3,6}\.[A-Z]{1,2})\b/g;
  const KNOWN_NON_TICKERS = new Set([
    // Indicators / metrics
    "RSI", "VIX", "FMI", "ETF", "AI", "EPS", "FCF", "ROE", "ROIC", "ROA", "PE", "PER", "PEG", "EBITDA", "TAM", "ATR", "MA", "EMA", "SMA", "DCA", "DCF", "ARR", "MRR", "FX", "GDP", "CPI", "CAC", "LTV", "IPO", "MA",
    // Currencies
    "USA", "EUR", "USD", "GBP", "JPY", "CHF", "DKK", "SEK", "NOK", "CNY", "HKD", "KRW", "TWD", "SGD", "CAD", "AUD", "NZD", "INR", "BRL", "ZAR",
    // Trading actions / signals — CRITICAL: these were turning into "tickers"
    "BUY", "SELL", "HOLD", "WAIT", "SKIP", "AVOID", "ENTER", "EXIT", "TRIM", "CUT", "ADD", "LONG", "SHORT", "FLAT", "PROFIT", "LOSS", "STOP", "TARGET", "ENTRY", "ENTER", "ENTRÉE",
    "STRONG_BUY", "STRONG_SELL", "STRONG", "WEAK",
    "COUPER", "GARDER", "PRENDRE", "RENFORCER", "VENDRE", "ACHETER", "POSITIF", "NEGATIF", "NEUTRE",
    // Risk levels
    "LOW", "MID", "HIGH", "MEDIUM", "VERY", "MAX", "MIN", "RISK", "CRITIQUE", "SEVERE", "DANGER",
    // Briefing keywords
    "URGENT", "ACTION", "ACTIONS", "ALERTE", "OFFENSIF", "DEFENSIF", "DÉFENSIF", "DAY", "PLAY", "PLAYS", "SWING", "TRADE", "TRADES", "LT", "PF", "FOMO", "BULL", "BEAR", "TIE",
    "CONVICTION", "RADAR", "TICKER", "TICKERS", "BUZZ", "NEWS", "FLASH", "MEMO", "RECAP",
    "VERDICT", "JOUR", "BRIEFING", "EXECUTIVE", "SUMMARY", "RÉSUMÉ", "EXEC",
    "MOAT", "MARGE", "TAUX", "VOL", "VOLUME", "GAINER", "LOSER", "MOVER", "BREAKOUT", "PULLBACK",
    // Companies (titles / labels)
    "CEO", "CFO", "CTO", "COO", "CMO", "CIO", "CRO", "BOARD", "FOUNDER", "PRESIDENT",
    // Institutions / acronyms
    "SEC", "FDA", "FED", "BCE", "ECB", "OPEC", "NATO", "BRICS", "UN", "WHO", "ESG", "ATH", "ATL", "PIB", "PMI", "YTD", "YOY", "QOQ", "MOM",
    // Common French / English fillers in MAJ
    "OK", "VS", "ET", "OU", "LE", "LA", "UN", "UNE", "LES", "LES", "DES", "QUI", "QUE", "POUR", "EST", "PAR", "SUR", "DANS", "PLUS", "TOUS", "AVEC", "CETTE", "CES", "MAIS", "AUX", "DU", "AU",
    // Common bullet labels
    "PRIX", "COURS", "CAP", "MCAP", "MARKET", "SHARE", "SHARES", "ALERT", "WARN", "INFO",
    "TOP", "BOTTOM", "NEW", "OLD", "BEST", "WORST", "FIRST", "LAST", "NEXT", "PREV",
    // Macro / sectors
    "TECH", "SEMI", "BIO", "MED", "BANK", "OIL", "GAS", "EV", "AI", "ML", "DATA", "CLOUD", "SAAS", "B2B", "B2C",
    // Generic uppercase verbs leaking into briefing
    "NEW", "OPEN", "CLOSE", "PRE", "POST", "AFTER", "BEFORE",
  ]);

  const renderInline = (text) => {
    const parts = [];
    let lastIndex = 0;
    const boldAndTickerRegex = /(\*\*[^*]+\*\*)|(\b[A-Z]{2,5}(?:\.[A-Z]{1,2})?(?:-[A-Z]{1,2}(?:\.[A-Z]{2})?)?)\b|(\b\d{3,6}\.[A-Z]{1,2})\b/g;
    let match;
    
    // Helper to check if a string is a ticker
    const isTicker = (str) => {
      if (!str || KNOWN_NON_TICKERS.has(str)) return false;
      return /^[A-Z]{2,5}(?:\.[A-Z]{1,2})?(?:-[A-Z]{1,2}(?:\.[A-Z]{2})?)?$/.test(str) || /^\d{3,6}\.[A-Z]{1,2}$/.test(str);
    };

    // Helper to render clickable ticker
    const renderTicker = (ticker, key) => {
      if (onAnalyzeTicker) {
        return (
          <button
            key={key}
            onClick={(e) => { e.stopPropagation(); onAnalyzeTicker(ticker); }}
            className="text-amber-400 hover:text-amber-300 font-bold underline underline-offset-2 decoration-amber-400/30 hover:decoration-amber-400/60 transition-colors cursor-pointer"
            data-testid={`briefing-ticker-${ticker}`}
          >
            {ticker}
          </button>
        );
      }
      return <span key={key}>{ticker}</span>;
    };
    
    while ((match = boldAndTickerRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={`t-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      
      if (match[1]) {
        // Bold text — check if it contains a ticker to make it clickable
        const boldContent = match[1].slice(2, -2);
        // Extract ticker from bold text like "CARL-B.CO (Carlsberg)" or just "PLTR"
        const tickerMatch = boldContent.match(/^([A-Z]{2,5}(?:\.[A-Z]{1,2})?(?:-[A-Z]{1,2}(?:\.[A-Z]{2})?)?)(\s|$|\()/);
        const numTickerMatch = boldContent.match(/^(\d{3,6}\.[A-Z]{1,2})(\s|$|\()/);
        const extractedTicker = tickerMatch?.[1] || numTickerMatch?.[1];
        
        if (extractedTicker && isTicker(extractedTicker)) {
          // Render ticker part as clickable, rest as bold
          const rest = boldContent.slice(extractedTicker.length);
          parts.push(renderTicker(extractedTicker, `btk-${match.index}`));
          if (rest) parts.push(<strong key={`br-${match.index}`} className="text-white font-semibold">{rest}</strong>);
        } else {
          parts.push(<strong key={`b-${match.index}`} className="text-white font-semibold">{boldContent}</strong>);
        }
      } else {
        const ticker = match[2] || match[3];
        if (ticker && !KNOWN_NON_TICKERS.has(ticker) && onAnalyzeTicker) {
          parts.push(renderTicker(ticker, `tk-${match.index}`));
        } else {
          parts.push(<span key={`s-${match.index}`}>{ticker}</span>);
        }
      }
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(<span key={`e-${lastIndex}`}>{text.slice(lastIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : text;
  };

  const renderBriefing = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-1.5" />;
      if (/^(\d+\.|#{1,3}\s|[A-Z]{3,}[\s:]|\*\*)/.test(trimmed)) {
        return <div key={i} className="text-[11px] font-bold text-white mt-2 first:mt-0">{renderInline(trimmed.replace(/^#+\s*/, ""))}</div>;
      }
      if (trimmed.startsWith("-") || trimmed.startsWith("•")) {
        return <div key={i} className="text-[10px] text-slate-300 pl-2 leading-relaxed flex gap-1"><span className="text-slate-500 shrink-0">-</span><span>{renderInline(trimmed.replace(/^[-•]\s*/, ""))}</span></div>;
      }
      return <div key={i} className="text-[10px] text-slate-300 leading-relaxed">{renderInline(trimmed)}</div>;
    });
  };

  return (
    <div className={`glass-card rounded-xl overflow-hidden mb-4 border ${mt ? borderCls : "border-slate-700/30"}`} data-testid="daily-briefing">
      {/* Header bar with market indicators */}
      <div className="px-3 py-2 bg-gradient-to-r from-slate-800/80 to-slate-900/80 border-b border-slate-700/40">
        {/* Top row: Briefing title + Score + VIX/RSI + Signals */}
        <div className="flex items-center gap-2 cursor-pointer flex-wrap" onClick={() => setExpanded(!expanded)}>
          <FileText className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-sm font-bold text-white">Briefing</span>
          <span className="text-[9px] text-slate-500">{new Date().toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span>

          {mt && (
            <div className="flex items-center gap-0.5 ml-1" data-testid="briefing-market-score">
              <Activity className={`w-3 h-3 ${colorCls}`} />
              <span className={`text-xs font-black ${colorCls}`}>{mt.timing_score}</span>
              <span className="text-[7px] text-slate-500">/100</span>
              <span className={`text-[8px] font-bold uppercase ml-0.5 ${colorCls}`}>{verdict}</span>
            </div>
          )}

          {vix && (
            <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-700/40" data-testid="briefing-vix">
              <span className="text-[7px] text-slate-500 font-bold">VIX</span>
              <span className={`text-[10px] font-bold ${vix < 20 ? "text-emerald-400" : vix < 30 ? "text-amber-400" : "text-red-400"}`}>{vix}</span>
            </div>
          )}

          {/* 🆕 CRASH STATUS — inline next to VIX */}
          <div className="relative" data-testid="crash-status-wrapper">
            <button
              onClick={(e) => { e.stopPropagation(); setCrashOpen((o) => !o); }}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wide transition-all hover:brightness-110 ${crashStatus.color}`}
              data-testid="crash-status-badge"
              title={crashStatus.summary}
            >
              <CrashIcon className="w-2.5 h-2.5" />
              {crashStatus.label}
            </button>
            {crashOpen && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="fixed sm:absolute z-50 sm:top-full sm:left-0 sm:mt-1 inset-x-2 sm:inset-x-auto top-16 sm:w-[520px] max-h-[80vh] bg-slate-900 border border-slate-700 rounded-lg shadow-2xl p-4 text-left overflow-hidden flex flex-col"
                data-testid="crash-status-popover"
              >
                <div className={`flex items-center gap-2 mb-3 pb-2 border-b border-slate-700/60`}>
                  <CrashIcon className={`w-4 h-4 ${crashStatus.level === "CALME" ? "text-emerald-400" : crashStatus.level === "VIGILANCE" ? "text-amber-400" : crashStatus.level === "ALERTE" ? "text-orange-400" : "text-red-400"}`} />
                  <span className="text-sm font-black text-white uppercase">{crashStatus.label}</span>
                  <span className="text-[10px] text-slate-500 ml-auto">VIX {vix || "?"} · S&P sem {spWeek >= 0 ? "+" : ""}{spWeek || 0}%</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed mb-3">{crashStatus.summary}</p>
                <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Plan d'action sur TON portefeuille</div>
                {crashActionsLoading && (
                  <div className="text-xs text-slate-500 italic py-2 flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Analyse de tes positions en cours (peut prendre 60s)...
                  </div>
                )}
                {!crashActionsLoading && crashActions && crashActions.actions && crashActions.actions.length > 0 ? (
                  <div className="space-y-3 overflow-y-auto pr-1 flex-1">
                    {crashActions.actions.map((a, ai) => (
                      <div key={ai} className="border-l-2 border-amber-500/40 pl-3">
                        <div className="text-xs font-bold text-white leading-snug mb-1">▸ {a.title}</div>
                        <div className="text-[11px] text-slate-400 italic mb-1.5">{a.detail}</div>
                        {(a.tickers || []).length > 0 && (
                          <ul className="space-y-1 text-[11px]">
                            {a.tickers.slice(0, 12).map((t, ti) => (
                              <li key={ti} className="text-slate-300 flex items-start gap-1.5 flex-wrap">
                                <span className="font-bold text-emerald-300 inline-block min-w-[60px]">{t.t}</span>
                                {t.broker && <span className="text-slate-500 text-[10px]">({t.broker})</span>}
                                {t.qty_to_sell !== undefined && (
                                  <span className="text-red-300">
                                    vendre <span className="font-bold">{t.qty_to_sell}/{t.qty}</span> @ ${t.price?.toFixed(2)}
                                    {t.value_freed !== undefined && <span className="text-emerald-400 ml-1">→ +${t.value_freed?.toLocaleString()}</span>}
                                  </span>
                                )}
                                {t.stop !== undefined && t.qty_to_sell === undefined && (
                                  <span className="text-amber-300">
                                    stop <span className="font-bold">${t.stop?.toFixed(2)}</span>
                                    <span className="text-slate-500 ml-1">(actuel ${t.price?.toFixed(2)})</span>
                                  </span>
                                )}
                                {t.pnl_pct !== undefined && t.qty_to_sell === undefined && t.stop === undefined && (
                                  <span className={t.pnl_pct >= 0 ? "text-emerald-300" : "text-red-300"}>
                                    P&L {t.pnl_pct >= 0 ? "+" : ""}{t.pnl_pct}%
                                    {t.value !== undefined && <span className="text-slate-500 ml-1">(${t.value?.toLocaleString()})</span>}
                                  </span>
                                )}
                              </li>
                            ))}
                            {a.tickers.length > 12 && (
                              <li className="text-slate-500 italic text-[10px]">... +{a.tickers.length - 12} autres positions</li>
                            )}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : !crashActionsLoading && (
                  <ul className="space-y-1 text-xs text-slate-300">
                    {crashStatus.actions.map((a, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-slate-600">▸</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 pt-2 border-t border-slate-700/60 text-[10px] text-slate-500">
                  Rappel : une correction de <span className="text-amber-400 font-semibold">-8% à -16%</span> est statistiquement normale 2-3x/an et repart ensuite. C'est pas un crash, c'est de la respiration.
                </div>
                <button
                  onClick={() => setCrashOpen(false)}
                  className="mt-2 w-full text-xs text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded px-2 py-1.5 transition-colors"
                  data-testid="crash-status-close"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>

          {rsi && (
            <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-700/40" data-testid="briefing-rsi">
              <span className="text-[7px] text-slate-500 font-bold">RSI</span>
              <span className={`text-[10px] font-bold ${rsi < 30 ? "text-emerald-400" : rsi > 70 ? "text-red-400" : "text-slate-300"}`}>{rsi}</span>
            </div>
          )}

          {mt?.signals?.slice(0, 3).map((sig, i) => (
            <span key={i} className={`hidden lg:inline-block text-[7px] font-bold px-1 py-0.5 rounded border ${sig.impact === "POSITIF" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/25" : sig.impact === "NEGATIF" ? "bg-red-500/10 text-red-400 border-red-500/25" : sig.impact === "CONTRARIAN" ? "bg-purple-500/10 text-purple-300 border-purple-500/25" : "bg-slate-500/10 text-slate-400 border-slate-500/20"}`} data-testid={`briefing-signal-${i}`}>
              {sig.signal}
            </span>
          ))}

          <div className="ml-auto flex items-center gap-1 shrink-0">
            <button onClick={(e) => { e.stopPropagation(); fetchBriefing(true); }} className="text-slate-400 hover:text-white transition-colors p-0.5" data-testid="refresh-briefing">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </button>
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
          </div>
        </div>

        {/* Global indices row */}
        {globalIndices.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-slate-700/20 overflow-x-auto scrollbar-none" data-testid="global-indices-row">
            {globalIndices.map((idx) => {
              const isVix = idx.name === "VIX" || idx.symbol === "^VIX";
              // VIX: down = good (green), up = bad (red) — inverse of normal
              const changeColor = isVix
                ? (idx.day_change <= 0 ? "text-emerald-400" : "text-red-400")
                : (idx.day_change >= 0 ? "text-emerald-400" : "text-red-400");
              const weekColor = isVix
                ? (idx.week_change <= 0 ? "text-emerald-500/50" : "text-red-500/50")
                : (idx.week_change >= 0 ? "text-emerald-500/50" : "text-red-500/50");
              return (
              <div key={idx.symbol} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700/25 shrink-0" data-testid={`index-${idx.name.replace(/\s/g, '-')}`}>
                {isVix ? (
                  <span className="text-[8px]" title="Indice de volatilite (peur)">
                    <Activity className="w-2.5 h-2.5 text-amber-400 inline" />
                  </span>
                ) : (
                  <span className="text-[8px]">{flagEmoji(idx.flag)}</span>
                )}
                <span className="text-[8px] text-slate-400 font-bold">{idx.name}</span>
                <span className={`text-[9px] font-bold ${changeColor}`}>
                  {idx.day_change >= 0 ? "+" : ""}{idx.day_change}%
                </span>
                <span className={`text-[7px] ${weekColor}`}>
                  sem {idx.week_change >= 0 ? "+" : ""}{idx.week_change}%
                </span>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Briefing content */}
      {expanded && (
        <div className="px-4 py-3 sm:max-h-[420px] sm:overflow-y-auto custom-scrollbar">
          <DeskWatchlist compact />
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
              <span className="text-xs text-slate-400">Generation du briefing IA...</span>
            </div>
          ) : !loaded && !briefing ? (
            <div className="flex flex-col items-center justify-center py-6 gap-3" data-testid="briefing-load-prompt">
              <FileText className="w-8 h-8 text-slate-600" />
              <p className="text-xs text-slate-500">Cliquez pour charger le briefing du jour</p>
              <button onClick={fetchBriefing} className="px-4 py-2 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-bold hover:bg-amber-500/25 transition-colors flex items-center gap-2" data-testid="load-briefing-btn">
                <RefreshCw className="w-3.5 h-3.5" />
                Charger le Briefing
              </button>
            </div>
          ) : (
            <>
              {/* AI Context: secteurs + strategy from market timing */}
              {mt?.ai_context && (
                <div className="mb-2 pb-2 border-b border-slate-700/30">
                  <div className="text-[10px] text-slate-300 leading-relaxed mb-1">{mt.ai_context.contexte}</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {mt.ai_context.secteurs_bull?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-[7px] text-emerald-500 font-bold">BULL</span>
                        {mt.ai_context.secteurs_bull.map((s, i) => <span key={i} className="text-[8px] px-1 py-px rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{s}</span>)}
                      </div>
                    )}
                    {mt.ai_context.secteurs_bear?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-[7px] text-red-500 font-bold">BEAR</span>
                        {mt.ai_context.secteurs_bear.map((s, i) => <span key={i} className="text-[8px] px-1 py-px rounded bg-red-500/10 text-red-400 border border-red-500/20">{s}</span>)}
                      </div>
                    )}
                    {mt.ai_context.catalyseurs?.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[7px] text-slate-500 font-bold">CATALYSEURS</span>
                        {mt.ai_context.catalyseurs.map((c, i) => <span key={i} className="text-[8px] text-slate-400 bg-slate-800/50 px-1 py-px rounded">{c}</span>)}
                      </div>
                    )}
                  </div>
                  {mt.ai_context.strategie && (
                    <div className="flex items-start gap-1.5 mt-1.5 px-2 py-1 bg-cyan-500/5 border border-cyan-500/15 rounded">
                      <AlertTriangle className="w-3 h-3 text-cyan-400 mt-px shrink-0" />
                      <span className="text-[9px] text-cyan-300 leading-relaxed">{mt.ai_context.strategie}</span>
                    </div>
                  )}
                </div>
              )}
              {briefing && briefing.startsWith("Briefing indisponible") ? (
                <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between" data-testid="briefing-error">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="text-xs text-amber-300">{briefing}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); fetchBriefing(true); }}
                    className="px-3 py-1 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold hover:bg-amber-500/30 transition-colors flex items-center gap-1 shrink-0 ml-2"
                    data-testid="retry-briefing-btn">
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reessayer
                  </button>
                </div>
              ) : (
                <div className="space-y-0">{renderBriefing(briefing)}</div>
              )}

              {/* PROCHAINES EARNINGS — vue rapide des sociétés qui rapportent dans les 10 prochains jours */}
              {upcomingEarnings?.days?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-700/40" data-testid="upcoming-earnings-briefing">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
                        Prochaines Earnings (10j)
                      </span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); fetchUpcomingEarnings(); }}
                      className="text-slate-500 hover:text-slate-300 transition-colors p-0.5"
                      data-testid="refresh-upcoming-earnings">
                      <RefreshCw className={`w-3 h-3 ${earningsLoading ? "animate-spin" : ""}`} />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {upcomingEarnings.days.map((day, i) => (
                      <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-800/40 border border-slate-700/40"
                        data-testid={`earnings-day-${day.date}`}>
                        <span className="text-[10px] font-bold text-amber-300 uppercase tracking-wide w-20 shrink-0">
                          {day.day_name}
                        </span>
                        <span className="text-[9px] text-slate-500 font-mono w-16 shrink-0">
                          {day.date.slice(5)}
                        </span>
                        <div className="flex-1 flex flex-wrap gap-1">
                          {day.tickers.map((t, j) => {
                            const riskColor = t.iv_risk_color || (
                              t.implied_move_pct >= 12 ? "rose"
                              : t.implied_move_pct >= 7 ? "orange"
                              : t.implied_move_pct != null ? "cyan" : null
                            );
                            const riskBadgeClass = {
                              "rose": "text-rose-300 bg-rose-500/10 border-rose-500/30",
                              "orange": "text-orange-300 bg-orange-500/10 border-orange-500/30",
                              "cyan": "text-cyan-300/90 bg-cyan-500/10 border-cyan-500/30",
                              "emerald": "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
                              "slate": "text-slate-400 bg-slate-700/30 border-slate-600/30",
                            }[riskColor] || "";
                            const tooltipParts = [];
                            if (t.in_portfolio) tooltipParts.push("Tu détiens");
                            if (t.is_mega) tooltipParts.push("Mega-cap");
                            if (t.implied_move_pct != null) {
                              tooltipParts.push(`Mouvement implicite ±${t.implied_move_pct}% (expiry ${t.implied_move_expiry})`);
                            }
                            if (t.historical_avg_move_pct != null) {
                              tooltipParts.push(`Historique 4 derniers earnings: ±${t.historical_avg_move_pct}%`);
                            }
                            if (t.iv_risk_interpretation) tooltipParts.push(t.iv_risk_interpretation);
                            return (
                              <button
                                key={j}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onAnalyzeTicker && t.ticker) onAnalyzeTicker(t.ticker);
                                }}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border transition-colors flex items-center gap-1 ${
                                  t.in_portfolio
                                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                                    : t.is_mega
                                    ? "bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25"
                                    : "bg-slate-700/30 border-slate-600/30 text-slate-300 hover:bg-slate-600/40"
                                }`}
                                title={tooltipParts.join(" · ")}
                                data-testid={`earnings-ticker-${t.ticker}`}
                              >
                                <span>{t.in_portfolio && "⭐"}{t.ticker}</span>
                                {t.implied_move_pct != null && (
                                  <span className={`text-[9px] px-1 rounded border font-normal ${riskBadgeClass}`}>
                                    ±{t.implied_move_pct}%
                                    {t.iv_risk_label === "EXPENSIVE" && " 🚨"}
                                    {t.iv_risk_label === "CHEAP" && " 💎"}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[9px] text-slate-600 mt-1.5 px-1 flex flex-wrap gap-2">
                    <span className="text-emerald-400">⭐ Tes positions</span>
                    <span className="text-amber-400">🏆 Mega-caps</span>
                    <span><span className="text-cyan-300">±X%</span> mouvement attendu</span>
                    <span className="text-rose-300">🚨 EXPENSIVE = options surévaluées vs historique</span>
                    <span className="text-emerald-300">💎 CHEAP = opportunité</span>
                  </div>
                </div>
              )}
              {earningsLoading && !upcomingEarnings && (
                <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-500">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Chargement des prochaines earnings…
                </div>
              )}

              </>
          )}

        </div>
      )}
      <BriefChatPanel owner={owner} />
    </div>
  );
}
